/**
 * Log Level Management System
 *
 * Provides centralized log level configuration and management with:
 * - Environment-based default levels
 * - Session storage persistence
 * - Runtime level changes
 * - Change notifications
 */

import { LogLevel } from '@soteria/logger/browser';
export { LogLevel };

export interface LogLevelConfig {
  level: LogLevel;
  environment: string;
  source: 'environment' | 'session' | 'default';
  timestamp: number;
}

// Internal state
let currentConfig: LogLevelConfig;
const listeners: Array<(config: LogLevelConfig) => void> = [];

// Environment-based defaults
const DEFAULT_LEVELS: Record<string, LogLevel> = {
  production: LogLevel.WARN,
  staging: LogLevel.INFO,
  development: LogLevel.DEBUG,
  test: LogLevel.ERROR,
};

/**
 * Parse log level from string or number
 */
export function parseLogLevel(value: string | number): LogLevel | null {
  if (typeof value === 'string') {
    const upperValue = value.toUpperCase();

    // Try direct string mapping
    if (upperValue in LogLevel) {
      return LogLevel[upperValue as keyof typeof LogLevel];
    }

    // Try numeric string parsing (for backwards compatibility)
    const numericValue = parseInt(value, 10);

    if (!isNaN(numericValue)) {
      const levelMap: Record<number, LogLevel> = {
        0: LogLevel.ERROR,
        1: LogLevel.WARN,
        2: LogLevel.INFO,
        3: LogLevel.DEBUG,
        4: LogLevel.TRACE,
      };

      return levelMap[numericValue] || null;
    }
  }

  // Handle direct LogLevel values
  if (typeof value === 'object' && Object.values(LogLevel).includes(value as LogLevel)) {
    return value as LogLevel;
  }

  return null;
}

/**
 * Get environment name from various sources
 */
function getEnvironment(): string {
  // Use Vite's import.meta.env
  return import.meta.env.MODE || import.meta.env.VITE_ENV || 'development';
}

/**
 * Load configuration from session storage
 */
function loadFromSessionStorage(): LogLevelConfig | null {
  try {
    const stored = sessionStorage.getItem('soteria-log-level');

    if (stored) {
      const parsed = JSON.parse(stored);
      const level = parseLogLevel(parsed.level);

      if (level !== null) {
        return {
          level,
          environment: parsed.environment || getEnvironment(),
          source: 'session',
          timestamp: parsed.timestamp || Date.now(),
        };
      }
    }
  } catch (error) {
    console.warn('Failed to load log level from session storage:', error);
  }

  return null;
}

/**
 * Save configuration to session storage
 */
function saveToSessionStorage(config: LogLevelConfig): void {
  try {
    sessionStorage.setItem('soteria-log-level', JSON.stringify(config));
  } catch (error) {
    console.warn('Failed to save log level to session storage:', error);
  }
}

/**
 * Load configuration from environment variables
 */
function loadFromEnvironment(): LogLevelConfig | null {
  // Use Vite's import.meta.env for environment variables
  const envLevel = import.meta.env.VITE_DEFAULT_LOG_LEVEL;

  if (envLevel) {
    const level = parseLogLevel(envLevel);

    if (level !== null) {
      return {
        level,
        environment: getEnvironment(),
        source: 'environment',
        timestamp: Date.now(),
      };
    }
  }

  return null;
}

/**
 * Get default configuration for current environment
 */
function getDefaultConfig(): LogLevelConfig {
  const environment = getEnvironment();
  const level = DEFAULT_LEVELS[environment] || LogLevel.INFO;

  return {
    level,
    environment,
    source: 'default',
    timestamp: Date.now(),
  };
}

/**
 * Notify all listeners of configuration change
 */
function notifyListeners(): void {
  listeners.forEach(listener => {
    try {
      listener(currentConfig);
    } catch (error) {
      console.error('Error in log level change listener:', error);
    }
  });
}

/**
 * Initialize log level configuration
 * Priority: session storage > environment variables > defaults
 */
export function initializeLogLevel(): LogLevelConfig {
  // Try session storage first
  let config = loadFromSessionStorage();

  // Fall back to environment variables
  if (!config) {
    config = loadFromEnvironment();
  }

  // Fall back to defaults
  if (!config) {
    config = getDefaultConfig();
  }

  currentConfig = config;

  // Save to session storage if not already there
  if (config.source !== 'session') {
    saveToSessionStorage(config);
  }

  return config;
}

/**
 * Get current log level
 */
export function getCurrentLevel(): LogLevel {
  if (!currentConfig) {
    initializeLogLevel();
  }

  return currentConfig.level;
}

/**
 * Get current configuration
 */
export function getConfig(): LogLevelConfig {
  if (!currentConfig) {
    initializeLogLevel();
  }

  return { ...currentConfig };
}

/**
 * Set new log level
 */
export function setLevel(level: LogLevel): void {
  if (!Object.values(LogLevel).includes(level)) {
    throw new Error(`Invalid log level: ${level}`);
  }

  const newConfig: LogLevelConfig = {
    level,
    environment: currentConfig?.environment || getEnvironment(),
    source: 'session',
    timestamp: Date.now(),
  };

  currentConfig = newConfig;
  saveToSessionStorage(newConfig);
  notifyListeners();
}

/**
 * Reset to default level for current environment
 */
export function resetToDefault(): void {
  const defaultConfig = getDefaultConfig();

  currentConfig = defaultConfig;
  saveToSessionStorage(defaultConfig);
  notifyListeners();
}

/**
 * Check if a message at the given level should be logged
 * Uses the same logic as the browser logger for consistency
 */
export function shouldLog(messageLevel: LogLevel): boolean {
  const currentLevel = getCurrentLevel();

  // Define log levels in order of severity (most severe to least severe)
  const levelHierarchy = [LogLevel.ERROR, LogLevel.WARN, LogLevel.INFO, LogLevel.DEBUG, LogLevel.TRACE];
  const minLevelIndex = levelHierarchy.indexOf(currentLevel);
  const messageLevelIndex = levelHierarchy.indexOf(messageLevel);

  // Only log if message level is equal or more severe than current level
  // Lower index = more severe, so message level index should be <= current level index
  return messageLevelIndex <= minLevelIndex;
}

/**
 * Subscribe to log level changes
 */
export function onLevelChange(listener: (config: LogLevelConfig) => void): () => void {
  listeners.push(listener);

  // Return unsubscribe function
  return () => {
    const index = listeners.indexOf(listener);

    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
}

// Initialize on module load
if (typeof window !== 'undefined') {
  initializeLogLevel();
}
